/**
 * Admin and Engineering navigation config.
 *
 * Story 24.8 — `adminNavigation` is the Settings hub's ONE list of links and
 * their gates. The hub (`pages/SettingsHubPage.tsx`) groups its rows into the
 * `settingsHubCards` below and filters them with `visibleNavItems`; the
 * Sidebar and the phone More page draw the single role-open `settingsNavRow`
 * that opens the hub. There is no second list of gates anywhere: a row's
 * visibility on the hub is its flags here, and
 * `__tests__/adminNavigation.routes.test.ts` pins every row's route guard.
 * The Sidebar renders `superAdminNavigation` as the superuser-only
 * "Engineering" group (Story 24.5).
 *
 * Lives in its own module rather than inside `Sidebar.tsx` for two reasons:
 * the `react-refresh/only-export-components` lint rule forbids a component file
 * exporting a non-component, and the owner/superuser visibility suites need the
 * REAL list. They used to hand-copy the row names, so adding a gated row here
 * left them asserting a stale list and still passing (T-20260916-1 L5); they now
 * derive `ownerOnly` / `superuserOnly` from this value.
 *
 * T-20260916-3 — the module exports TWO row arrays: `adminNavigation` (the
 * tenant's settings links) and `superAdminNavigation` (the platform
 * operator's). Anything that reasons about "every nav row" must read BOTH —
 * see `__tests__/adminNavigation.routes.test.ts` and
 * `__tests__/Sidebar.superuser.test.tsx`, which both do.
 */
import {
  ArrowUpTrayIcon,
  BanknotesIcon,
  BellIcon,
  BuildingOffice2Icon,
  BuildingStorefrontIcon,
  ClockIcon,
  CurrencyDollarIcon,
  KeyIcon,
  LinkIcon,
  PuzzlePieceIcon,
  ScaleIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import {
  BanknotesIcon as BanknotesIconSolid,
  BuildingOffice2Icon as BuildingOffice2IconSolid,
  ScaleIcon as ScaleIconSolid,
} from '@heroicons/react/24/solid';
import { NAV_ICONS } from '@/lib/ui/navIcons';
import type { NavItem } from './SidebarSubmenu';

// The Settings hub's links, in hub order (Story 24.8, EXPERIENCE.md › Settings
// hub). Sentence-case labels.
//
// Story 24.8 — THREE ROWS ARE ROLE-OPEN, ON PURPOSE: Marketplace, Your team
// and (Story 29.3) Notifications. Marketplace's and Your team's `ownerOnly`
// flag (T-20260916-2 / Story 14.6) was only decluttering — a member had no
// Admin submenu at all — while their ROUTES were role-open by design
// (browsing the catalogue is open, only the toggle is owner-gated; the agents
// list is read-only for members). Notifications was born role-open: members
// see who gets notified read-only, and every write is owner-gated at the
// harness. The hub is role-open (ruled M-2), so a member now sees exactly
// those three links. All three are pinned as
// `ROLE_OPEN_BY_DESIGN` in `__tests__/adminNavigation.routes.test.ts`, which
// fails BY NAME on any other ungated row. Do not open another row here
// without its route and page moving with it — the nav flag only HIDES.
//
// Every other row is owner-only end to end: `ownerOnly` here, `<RequireOwner>`
// at the route in `App.tsx`, and a self-gate in its own page.
//
// 🔴 "GATED" IS NOT "ROUTE-GUARDED", and the two are not yet the same set.
// `/settings/scheduled-tasks` and `/settings/integrations` are still BARE
// routes in `App.tsx` defended only by their own page self-gate. They are
// defended — the page refuses and renders `OwnerAccessRequired` — but they
// refuse LATER and more loudly than a route guard, and a page that forgot its
// self-gate would be wide open with no second line. Route-guarding those two
// is deferred, so the correspondence is pinned instead:
// `__tests__/adminNavigation.routes.test.ts` asserts every `ownerOnly` href
// resolves to a `<RequireOwner>`-wrapped route and carries the two known
// exceptions as an explicit, documented allowlist — so a NEW row cannot join
// them silently.
//
// T-20260916-3 — `Prompt Management`, `Billing & Credits` and `Byte Cost` are
// not in this array; they live in `superAdminNavigation` below. This array has
// NO `superuserOnly` row (the suites assert that, so the two groups cannot
// quietly re-merge).
export const adminNavigation: NavItem[] = [
  // Story 24.8 — today's team page, moved from `/settings` (owner-only).
  { name: 'Team & roles', href: '/settings/team', icon: UsersIcon, ownerOnly: true },
  // Story 11.3 (architecture D7) — the Chief of Staff sends owners to the
  // Marketplace. Role-open (see the header): browsing is open, only the
  // enable/disable toggle inside the page is owner-gated. Do not wrap the route.
  { name: 'Marketplace', href: '/marketplace', icon: BuildingStorefrontIcon },
  // T-20260915-3 — per-plugin settings (switcher over every enabled plugin).
  // Owner-only since T-20260916-2: the page carries plugin enable/disable and
  // the settings form.
  { name: 'Plugin settings', href: '/settings/plugins', icon: PuzzlePieceIcon, ownerOnly: true },
  // Story 14.6 — role-open (see the header): members see the tenant's agents
  // read-only; only Archive and the skills section are owner-gated, on the
  // Details page. Do not wrap the routes.
  { name: 'Your team', href: '/admin/agents', icon: NAV_ICONS.yourTeam.outline },
  // Story 4.2 — platform integration credentials (the Zernio publishing key).
  // Owner-only: it acts on behalf of the whole organisation. Not gated on any
  // plugin being enabled — that is the point of moving the credential to the
  // platform namespace (D5).
  { name: 'Integrations', href: '/settings/integrations', icon: LinkIcon, ownerOnly: true },
  // T-20260916-8 — the `ama_` machine keys a service outside Amatis uses to
  // reach `/api/data`. Sits beside Integrations because it is the same kind of
  // thing (an organisation-wide credential), but the direction is reversed:
  // Integrations is a credential we hold for someone else's service, this is one
  // they hold for us. Owner-only end to end — all three routes carry
  // `requirePermission('api_keys.manage')`, which `permissions/can.ts:142` maps
  // to `['owner']` off the JWT role claim alone, so a superuser does NOT bypass
  // it and the page narrows to `isOwnerByRole()` behind its `<RequireOwner>`
  // route.
  { name: 'API keys', href: '/settings/api-keys', icon: KeyIcon, ownerOnly: true },
  { name: 'Import data', href: '/import/csv', icon: ArrowUpTrayIcon, ownerOnly: true },
  // Owner-only because the whole scheduled-jobs system (manual cron edits,
  // Run-now, enable/disable) is an owner-only feature by design.
  { name: 'Scheduled tasks', href: '/settings/scheduled-tasks', icon: ClockIcon, ownerOnly: true },
  { name: 'Usage', href: '/admin/observability/usage-llm-cost', icon: CurrencyDollarIcon, ownerOnly: true },
  // Story 29.3 — who gets notified. Role-open (see the header): members see
  // the contacts read-only; the page's controls and the harness PUTs are
  // owner-gated. Do not wrap the route.
  { name: 'Notifications', href: '/settings/notifications', icon: BellIcon },
];

/**
 * Story 24.8 — the ONE Settings row, drawn by the Sidebar (≥1024px) and the
 * phone More page. Role-open: `/settings` is the hub, which every role may
 * open (ruled M-2); the hub filters its own cards. No gate flags, on purpose —
 * `__tests__/adminNavigation.routes.test.ts` pins `/settings` as
 * `ROLE_OPEN_BY_DESIGN`.
 */
export const settingsNavRow: NavItem = {
  name: 'Settings',
  href: '/settings',
  icon: NAV_ICONS.settings.outline,
  activeIcon: NAV_ICONS.settings.solid,
};

/** The icon type the hub cards use (Heroicons satisfy it). */
export type IconComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>;

/** A Settings hub card: a titled group of `adminNavigation` rows, by href. */
export interface SettingsHubCard {
  id: string;
  title: string;
  description: string;
  icon: IconComponent;
  /** `adminNavigation` hrefs, in the order the card lists them. */
  hrefs: readonly string[];
}

/**
 * Story 24.8 — the hub's cards, in order (EXPERIENCE.md › Settings hub). Each
 * href names an `adminNavigation` row, and every row sits in exactly one card
 * (pinned by `__tests__/settingsHub.test.ts`). The cards carry NO gates of
 * their own: a card shows the rows its viewer may see, and disappears when
 * that is none.
 */
export const settingsHubCards: readonly SettingsHubCard[] = [
  {
    id: 'team',
    title: 'Team & roles',
    description: 'Invite people and choose what each person can do.',
    icon: UsersIcon,
    hrefs: ['/settings/team'],
  },
  {
    id: 'plugins',
    title: 'Plugins',
    description: 'Add plugins and change how each one works.',
    icon: PuzzlePieceIcon,
    hrefs: ['/marketplace', '/settings/plugins'],
  },
  {
    id: 'your-team',
    title: 'Your team',
    description: 'The agents who work for you and what they are doing.',
    icon: NAV_ICONS.yourTeam.outline,
    hrefs: ['/admin/agents'],
  },
  {
    id: 'connections',
    title: 'Connections',
    description: 'Link other services, manage API keys and bring in data.',
    icon: LinkIcon,
    hrefs: ['/settings/integrations', '/settings/api-keys', '/import/csv'],
  },
  {
    id: 'automation',
    title: 'Automation',
    description: 'Jobs your agents run on a schedule.',
    icon: ClockIcon,
    hrefs: ['/settings/scheduled-tasks'],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Choose who will hear about new work and approvals.',
    icon: BellIcon,
    hrefs: ['/settings/notifications'],
  },
  {
    id: 'usage',
    title: 'Usage & credit',
    description: 'Credit remaining and what it has been spent on.',
    icon: CurrencyDollarIcon,
    hrefs: ['/admin/observability/usage-llm-cost'],
  },
];

/** A hub card with the rows its viewer may see, in the card's href order. */
export interface VisibleSettingsCard extends SettingsHubCard {
  items: NavItem[];
}

/**
 * Story 24.8 — the hub's cards for this viewer. Each card's rows are looked
 * up in `adminNavigation` and filtered by the same `visibleNavItems` the
 * Sidebar uses; a card with nothing left is dropped (a link the viewer cannot
 * open is not drawn, and neither is an empty card).
 */
export function visibleSettingsCards(viewer: NavViewer): VisibleSettingsCard[] {
  return settingsHubCards.flatMap((card) => {
    const rows = card.hrefs
      .map((href) => adminNavigation.find((row) => row.href === href))
      .filter((row): row is NavItem => row !== undefined);
    const items = visibleNavItems(rows, viewer);
    return items.length > 0 ? [{ ...card, items }] : [];
  });
}

// Engineering group items (the former SuperAdmin submenu) — the PLATFORM
// operator's rows (T-20260916-3).
//
// Story 24.5 — rendered as the sidebar's "Engineering" group (A5). Each row
// carries an `activeIcon` (the solid glyph shown while its route is active),
// and Prompt Management takes `CommandLineIcon` because the group's own toggle
// now uses the wrench.
//
// `adminNavigation` is the tenant owner's set; these rows are not tenant
// concerns at all. Prompt Management edits the L1/L2 prompts every tenant runs on, Billing &
// Credits moves money across tenants, and Byte Cost is a platform ENGINEERING
// metric — per-turn context byte cost measures how the harness assembles
// prompts, which is Paul's ruling and the actual reason it moved. Sitting them
// under Admin told a tenant owner they were theirs.
//
// 🔴 THE REASON IS NOT "IT AGGREGATES CROSS-TENANT". An earlier draft of this
// header said so. The discriminator is whose CONCERN the number is, not whose
// rows it counts: per-turn context byte cost measures how WE assemble prompts,
// so it is ours; `Usage & LLM Cost` reports a tenant's own spend, so it is
// theirs. Apply the aggregation rule instead and you will move a row that is
// nobody's platform concern, and red
// `__tests__/adminNavigation.routes.test.ts`'s sibling case.
//
// 🔴 AND THE LIVE COUNTER-EXAMPLE IS GONE, WITH NO REPLACEMENT — which is why
// the rule above is stated positively rather than by contrast. It used to be
// `Observability` (`/admin/observability/manifest-emission-rate`): a
// cross-tenant query that was deliberately owner-visible, so it refuted the
// aggregation rule by existing. T-20260916-6 retired that page. `Byte Cost` is
// now the ONLY cross-tenant route left in `routes/admin-observability.ts` —
// llm-cost filters on the JWT-derived tenantId and search-engines touches no
// database — so the aggregation rule fits a surviving set of ONE, by
// coincidence. Do not read that coincidence as the rule, and do not substitute
// another row as the counter-example: none of the survivors has the property.
//
// 🔴 EVERY ROW HERE IS `superuserOnly`, and the flag is kept even though the
// whole group is gated: `SidebarSubmenu` filters per row, so the flag is what
// keeps the filter honest if a non-superuser row is ever added to this array,
// and it is what `__tests__/adminNavigation.routes.test.ts` reads to demand a
// `<RequireSuperuser>` route. Defence in depth, not redundancy to be tidied
// away.
//
// 🔴 BYTE COST MOVED GATE, NOT JUST HEADING (T-20260916-3). It was `ownerOnly`
// here, `<RequireOwner>` in `App.tsx` and `ownerGate` on the handler. All three
// changed together, and they have to: a nav flag only HIDES, so flipping this
// row alone would have left every tenant owner holding the URL and the API
// while the menu claimed the surface was superuser-only. The consequence, stated
// rather than discovered: a tenant owner who is not a superuser can no longer
// see per-turn context byte cost at all.
//
// The group's own visibility is `Sidebar.tsx`'s `isSuperuser` prop; with nothing
// visible `SidebarSubmenu` returns null (T-20260916-2), so a non-superuser gets
// no Engineering heading in the DOM rather than an empty panel behind a live
// chevron.
export const superAdminNavigation: NavItem[] = [
  { name: 'Prompt Management', href: '/admin/prompts', icon: NAV_ICONS.promptManagement.outline, activeIcon: NAV_ICONS.promptManagement.solid, superuserOnly: true },
  // Tenant Credit Balance, Pass 2 — superuser-only credit management (balance,
  // ledger, manual top-up/adjust). Platform/cross-tenant concern, not tenant-level.
  { name: 'Billing & Credits', href: '/admin/billing', icon: BanknotesIcon, activeIcon: BanknotesIconSolid, superuserOnly: true },
  // Story 10.4 — superuser tenant admin: set a tenant's organisation kind.
  { name: 'Tenants', href: '/admin/tenants', icon: BuildingOffice2Icon, activeIcon: BuildingOffice2IconSolid, superuserOnly: true },
  // Story 24.2's per-tier context byte-cost dashboard; re-gated owner →
  // superuser by T-20260916-3 (see the header above).
  { name: 'Byte Cost', href: '/admin/observability/per-tier-byte-cost', icon: ScaleIcon, activeIcon: ScaleIconSolid, superuserOnly: true },
];

/** Who is looking — the two flags every gated row is filtered by. */
export interface NavViewer {
  isOwner: boolean;
  isSuperuser: boolean;
}

/**
 * The rows of `items` this viewer may see (T-20260916-4).
 *
 * ONE definition, used by `SidebarSubmenu` (to decide what to render, and
 * whether to render itself at all) AND by `Sidebar` (to decide whether the
 * pinned utility zone has any content, and therefore whether its divider
 * should exist). It lives here because this module is already the non-component
 * home for nav data — `react-refresh/only-export-components` forbids either
 * component file exporting it — and because the alternative was a second copy
 * of the predicate in `Sidebar.tsx`, which is precisely the drift this module
 * was created to stop.
 *
 * Story PPA-2.1's rule: conditional rendering, never CSS hiding, so a gated row
 * is absent from the DOM rather than merely invisible to the eye.
 */
export function visibleNavItems(items: NavItem[], viewer: NavViewer): NavItem[] {
  return items.filter((item) => {
    if (item.superuserOnly && !viewer.isSuperuser) return false;
    if (item.ownerOnly && !viewer.isOwner) return false;
    return true;
  });
}

/**
 * Story 24.5 — is `href` the current route, or an ancestor of it? ONE rule
 * for every sidebar row: Boards (`/dashboards`) lost its old exact-match
 * exception, so `/dashboards/:id` highlights it too.
 */
export function isNavHrefActive(pathname: string, href: string): boolean {
  return pathname === href || pathname.startsWith(href + '/');
}
